import { isEqual } from 'date-fns';
import { Component, useEffect, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import urlTemplate from 'url-template';
import type {
  AuctionsOfAuctionTileResponse,
  AuctionTilesResponse,
  SchedulesResponse,
  TransportAuctionServiceRootResponse,
} from 'src/apis/transport-auction/types';
import { useAxios } from 'src/hooks/use-axios';

function useSafeTransportAuctionServiceRoot() {
  const root = useAxios(
    (axios, baseConfig) =>
      axios.request<TransportAuctionServiceRootResponse>({
        ...baseConfig,
        url: PRISMA_CONFIG.transportAuction,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // catch everything
        return error;
      },
    }
  );

  useEffect(() => {
    root.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return root;
}

type AuctionTilesOptions = {
  url: string;
};

function useSafeAuctionTiles() {
  return useAxios(
    (axios, baseConfig, { url }: AuctionTilesOptions) =>
      axios.request<AuctionTilesResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // catch everything
        return error;
      },
    }
  );
}

type SchedulesOptions = {
  url: string;
};

function useSafeSchedules() {
  return useAxios(
    (axios, baseConfig, { url }: SchedulesOptions) =>
      axios.request<SchedulesResponse>({
        ...baseConfig,
        url,
        // usually we would filter the schedules here, but we keep it simple for the traffic simulation
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // catch everything
        return error;
      },
    }
  );
}

type AuctionTileDetailsOptions = {
  root: TransportAuctionServiceRootResponse;
  search: string;
};

function useSafeAuctionTileDetails() {
  return useAxios(
    async (axios, baseConfig, { root, search }: AuctionTileDetailsOptions) => {
      const auctionTilesLink = root._links.auctionTiles;
      if (!root._links.auctionTile) return null;
      if (!root._links.auctionsOfAuctionTile) return null;

      // only needed to find correct auction tile (this will slightly increase the traffic on this endpoint)
      const auctionTilesResponse = await axios.request<AuctionTilesResponse>({
        ...baseConfig,
        url: auctionTilesLink.href,
      });
      const auctionTiles = [
        ...auctionTilesResponse.data.shortTermAuctionTiles,
        ...auctionTilesResponse.data.longTermAuctionTiles,
      ];

      const oldParams = new URLSearchParams(search);
      const slot = oldParams.get('slot');
      const phase = oldParams.get('phase');
      const normalizedPhase =
        phase === 'CLOSED' ? 'WAITING_FOR_NEXT_ROUND' : phase;
      const periodType = oldParams.get('periodType');
      const runtimeFrom = oldParams.get('runtime-from');
      const runtimeTo = oldParams.get('runtime-to');
      const identifier = oldParams.get('identifier');

      // find correct auction tile on a best effort basis
      const auctionTile = auctionTiles.find(
        (tile) =>
          slot &&
          normalizedPhase &&
          periodType &&
          runtimeFrom &&
          runtimeTo &&
          tile.slotIndex.toString() === slot &&
          tile.state === normalizedPhase &&
          tile.productType === periodType &&
          isEqual(new Date(tile.minRuntimeStart), new Date(runtimeFrom)) &&
          isEqual(new Date(tile.maxRuntimeEnd), new Date(runtimeTo)) &&
          tile.customCalendarIdentifier === (identifier ?? undefined)
      );
      if (!auctionTile) return null;

      // we found an auction tile! now we fetch the auction tile details & auctions
      const auctionTilePromise = axios.request<AuctionTilesResponse>({
        ...baseConfig,
        url: urlTemplate
          .parse(root._links.auctionTile.href)
          .expand({ tileId: auctionTile.id }),
      });
      const auctionsOfAuctionTilePromise =
        axios.request<AuctionsOfAuctionTileResponse>({
          ...baseConfig,
          url: urlTemplate
            .parse(root._links.auctionsOfAuctionTile.href)
            .expand({ tileId: auctionTile.id }),
        });

      return Promise.all([auctionTilePromise, auctionsOfAuctionTilePromise]);
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        // catch everything
        return error;
      },
    }
  );
}

type ErrorBoundaryState = { error: unknown };

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: undefined };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override render() {
    if (this.state.error === undefined) return this.props.children;
    return null; // just swallow runtime errors
  }
}

/**
 * This component simulates real world traffic against the auction tiles endpoint.
 * We run it on all stages, but catch any kind of errors and don't use the responses.
 * The goal is to check real world metrics in our service.
 */
export const AuctionTilesTraffic: FC = () => {
  return (
    <ErrorBoundary>
      <TrackTraffic />
    </ErrorBoundary>
  );
};

const TrackTraffic: FC = () => {
  const root = useSafeTransportAuctionServiceRoot();
  const auctionTiles = useSafeAuctionTiles();
  const schedules = useSafeSchedules();
  const auctionTileDetails = useSafeAuctionTileDetails();

  const { pathname, hash, search } = useLocation();

  useEffect(() => {
    if (!root.response) return;
    const location = `${pathname}${hash}`;

    // fetch auction tiles
    if (
      location === '/' ||
      location === '/transport/auctions' ||
      location === '/platform/#/start' ||
      location === '/dashboard'
    ) {
      const auctionTilesLink = root.response.data._links.auctionTiles;
      auctionTiles.execute({ url: auctionTilesLink.href });
    }

    // fetch schedules
    if (location === '/transport/auctions') {
      const schedulesLink = root.response.data._links.schedules;
      if (schedulesLink)
        schedules.execute({
          url: urlTemplate.parse(schedulesLink.href).expand({}),
        });
    }

    // fetch auction tile & auctions
    if (location === '/transport/auctions/list') {
      // try to find correct auction tile on a best effort basis
      auctionTileDetails.execute({ root: root.response.data, search });
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [pathname, hash, root.response]);

  return null;
};
